import { Link } from 'react-router-dom';
import { calendarDayMs, currentCoverage } from '@ferro/core/modules/dog-profile/vaccination-coverage.js';
import { vaccinationStanding } from '@ferro/core/modules/dog-profile/vaccination-source.js';
import type { AdminDog } from '../../lib/api.js';
import { hasActiveMedication } from './MedicationList.js';

export type VaccinationStatusKind =
  | 'expired'
  | 'needs_verification'
  | 'up_to_date'
  | 'no_records';

export interface DogCardProps {
  readonly dog: AdminDog;
  readonly now?: Date;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function calculateAge(birthDateIso: string, now: Date): string {
  const birth = new Date(birthDateIso);
  const years = now.getFullYear() - birth.getFullYear();
  const months = now.getMonth() - birth.getMonth();
  const adjustedYears =
    months < 0 || (months === 0 && now.getDate() < birth.getDate())
      ? years - 1
      : years;
  if (adjustedYears < 1) {
    const totalMonths =
      (now.getFullYear() - birth.getFullYear()) * 12 +
      (now.getMonth() - birth.getMonth());
    return totalMonths <= 1 ? '< 1 month' : `${totalMonths} months`;
  }
  return adjustedYears === 1 ? '1 year' : `${adjustedYears} years`;
}

/**
 * #545 / #593 — vaccination-status derivation for the compact card badge.
 * Kept exported: consumed by DogCard's own test and available to other
 * dog-first surfaces (grep before removing).
 *
 * Ted #32a (E1.1, #1156): graded on each vaccine's CURRENT record through the
 * same core rule the server uses (`vaccination-coverage.ts`), so the card can
 * never say "Expired" for a dog whose lapsed copies sit beside a renewal.
 * Expiry is a calendar day (`@db.Date`): a shot is expired once its day is
 * before today's, never at the UTC-midnight instant that is still yesterday
 * evening in Denver.
 */
export function vaccinationStatus(
  dog: Pick<AdminDog, 'vaccinations'>,
  now: Date = new Date(),
): VaccinationStatusKind {
  if (dog.vaccinations.length === 0) return 'no_records';
  const current = currentCoverage(
    dog.vaccinations.map((v) => ({
      name: v.name,
      expiresAt: v.expires_at,
      dateGiven: v.date_given,
      verified: v.verified,
      source: v.source ?? null,
    })),
  );
  const todayMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const hasExpired = current.some((v) => {
    const day = calendarDayMs(v.expiresAt);
    return day !== null && day < todayMs;
  });
  if (hasExpired) return 'expired';
  // Ted #32a (E1.7, #1156) — "needs verification" is a task for the desk:
  // a record an owner entered. An unsigned Gingr or desk-typed record is not
  // that task, so it does not raise the badge.
  const hasUnverified = current.some(
    (v) => vaccinationStanding({ verified: v.verified, source: v.source }).kind === 'unverified',
  );
  if (hasUnverified) return 'needs_verification';
  return 'up_to_date';
}

function statusBadgeLabel(s: VaccinationStatusKind): string {
  switch (s) {
    case 'expired':
      return 'Expired';
    case 'needs_verification':
      return 'Needs verification';
    case 'up_to_date':
      return 'Up to date';
    case 'no_records':
      return 'No records';
  }
}

/**
 * Compact, dog-first link-card (#593 profile redesign). The whole card is a
 * single tap to the dog's full record at `/dogs/:dogId` (Lane A owns that
 * page). It shows only what's scannable at a glance — photo, name, a one-line
 * meta, and the two safety-relevant badges (vaccination status + on-meds).
 * The deep detail (vax table, meds list, behavior-note history, vet, emergency
 * authorization) lives on the pet page, one tap away — never expanded in place.
 */
export function DogCard({ dog, now = new Date() }: DogCardProps): JSX.Element {
  const status = vaccinationStatus(dog, now);
  const onMeds = hasActiveMedication(dog, now);
  const subtitleParts: string[] = [];
  if (dog.breed) subtitleParts.push(dog.breed);
  if (dog.size_category) subtitleParts.push(dog.size_category);
  if (dog.weight !== null) subtitleParts.push(`${dog.weight} lbs`);
  if (dog.birth_date) subtitleParts.push(calculateAge(dog.birth_date, now));
  const subtitle = subtitleParts.join(' · ') || 'Unknown breed';

  return (
    <Link
      to={`/dogs/${dog.id}`}
      className="ferro-dog-linkcard"
      data-dog-id={dog.id}
      aria-label={`${dog.name} — open full record`}
    >
      <span className="ferro-dog-card__avatar" aria-hidden="true">
        {dog.photo_url ? (
          <img
            src={dog.photo_url}
            alt=""
            className="ferro-dog-card__avatar-img"
          />
        ) : (
          <span className="ferro-dog-card__avatar-initials">
            {initials(dog.name)}
          </span>
        )}
      </span>

      <span className="ferro-dog-card__title">
        <span className="ferro-dog-card__name">{dog.name}</span>
        <span className="ferro-dog-card__subtitle">{subtitle}</span>
      </span>

      <span className="ferro-dog-card__chips">
        {/* #653 — a desk-created pet is honestly marked as unfinished
            rather than sitting in the list looking identical to a fully
            completed profile (Principle 5: never lie about data). The
            server derives this; the card never guesses. `title` carries
            the plain sentence naming exactly what's still needed. */}
        {dog.intake_completeness &&
        !dog.intake_completeness.is_complete ? (
          <span
            className="ferro-dog-card__intake-chip"
            data-testid="dog-incomplete-chip"
            title={dog.intake_completeness.summary ?? 'Profile incomplete'}
            aria-label={dog.intake_completeness.summary ?? 'Profile incomplete'}
          >
            Incomplete
          </span>
        ) : null}
        {onMeds ? (
          <span
            className="ferro-dog-card__meds-chip"
            data-on-meds="true"
            aria-label="On medication"
            title="On medication"
          >
            <span aria-hidden="true" className="ferro-dog-card__meds-chip-dot" />
            On meds
          </span>
        ) : null}
        <span
          className={`ferro-dog-card__status ferro-dog-card__status--${status}`}
          data-status={status}
        >
          {statusBadgeLabel(status)}
        </span>
      </span>

      <span className="ferro-dog-linkcard__arrow" aria-hidden="true">
        →
      </span>
    </Link>
  );
}
